import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Image, Music, Loader2, HardDrive } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const S3FileManager = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchFiles();
  }, [navigate]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/s3/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Error fetching S3 files:', error);
      toast.error('Failed to load S3 files');
    } finally {
      setLoading(false);
    }
  };

  const deleteFile = async (s3Key) => {
    if (!window.confirm(`Are you sure you want to delete this file?\n${s3Key}`)) {
      return;
    }

    setDeleting(s3Key);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/s3/files/${encodeURIComponent(s3Key)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('File deleted successfully');
      fetchFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    } finally {
      setDeleting(null);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileIcon = (key) => {
    if (key.includes('/audio/') || key.endsWith('.mp3') || key.endsWith('.webm')) {
      return <Music className="w-5 h-5 text-purple-500" />;
    }
    if (key.includes('/images/') || key.endsWith('.jpg') || key.endsWith('.png')) {
      return <Image className="w-5 h-5 text-blue-500" />;
    }
    return <HardDrive className="w-5 h-5 text-gray-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary/20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/20 py-12">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="mb-6">
            <h1 className="text-3xl font-heading font-bold text-primary mb-2">
              S3 File Manager
            </h1>
            <p className="text-muted-foreground">
              Manage files stored in your AWS S3 bucket. View and delete orphaned files.
            </p>
          </div>

          {files.length === 0 ? (
            <div className="text-center py-12">
              <HardDrive className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No files found in S3 bucket</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-semibold text-foreground">File</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Size</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Last Modified</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file) => (
                    <tr key={file.key} className="border-b border-border hover:bg-secondary/30">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {getFileIcon(file.key)}
                          <span className="text-sm font-mono text-foreground truncate max-w-md">
                            {file.key}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {formatSize(file.size)}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {file.last_modified ? new Date(file.last_modified).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => deleteFile(file.key)}
                          disabled={deleting === file.key}
                          className="inline-flex items-center gap-2 px-3 py-1 bg-destructive text-white rounded hover:bg-destructive/90 transition-colors disabled:opacity-50"
                        >
                          {deleting === file.key ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Total files: {files.length}
            </p>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default S3FileManager;
